// Section 19 and 14: Rumour of a better valley. 3% per season, Spring bias, only while settlement
// trust is below 45. The 1 to 3 least trusting households consider leaving. Choices: address it
// (a gathering: 1 food per person in those households, their trust +4) or let them go (they leave
// at once, this Spring or the next Summer, reason "rumour").
// Constants: config.events.rumour.
import type { Config } from '../../config';
import {
  choiceOutcome,
  departureTurn,
  eventEntry,
  eventPrompt,
  eventRng,
} from '../../core/content-helpers';
import { settlementTrust } from '../../core/queries';
import { defineEvent, noModifiers, type EventChoiceDef } from '../../core/registry';
import type { Delta, EventPrompt, Household, HouseholdId, ReadContext } from '../../core/types';

function wavering(ctx: ReadContext, min: number, max: number): Household[] {
  const scored = (Object.keys(ctx.state.households).sort() as HouseholdId[])
    .map((id) => ctx.state.households[id])
    .filter((h): h is Household => h !== undefined && h.memberIds.length > 0)
    .map((h) => {
      let trust = 0;
      for (const id of h.memberIds) trust += ctx.state.people[id]?.trust ?? 0;
      return { h, trust: trust / h.memberIds.length };
    })
    .sort((a, b) => a.trust - b.trust || (a.h.id < b.h.id ? -1 : 1));
  const n = eventRng(ctx, 'rumour', 'households').int(min, max);
  return scored.slice(0, n).map((s) => s.h);
}

function members(ctx: ReadContext, prompt: EventPrompt): string[] {
  const out: string[] = [];
  for (const id of prompt.subjectIds) {
    const h = ctx.state.households[ctx.state.people[id]?.householdId ?? 'h0'];
    if (h !== undefined) out.push(...h.memberIds);
  }
  return [...new Set(out)].sort();
}

export function rumour(config: Config) {
  const c = config.events.rumour;
  const choices: EventChoiceDef[] = [
    {
      id: 'address',
      label: 'Address it',
      preview: (ctx, prompt) => [
        {
          key: 'rumour-address',
          params: {
            food: members(ctx, prompt).length * c.addressFoodPerPerson,
            trust: c.addressTrust,
          },
        },
      ],
      apply: (ctx, prompt) => {
        const people = members(ctx, prompt);
        const out: Delta[] = [
          {
            kind: 'stock',
            resource: 'food',
            amount: -people.length * c.addressFoodPerPerson,
            flow: 'consumption',
          },
        ];
        for (const id of people)
          out.push({
            kind: 'person',
            personId: id as `p${string}`,
            field: 'trust',
            amount: c.addressTrust,
            source: 'event:rumour',
          });
        out.push(choiceOutcome(prompt, 'address', { people: people.length }));
        return out;
      },
    },
    {
      id: 'let-go',
      label: 'Let them go',
      preview: (ctx, prompt) => [
        { key: 'rumour-let-go', params: { people: members(ctx, prompt).length } },
      ],
      apply: (ctx, prompt) => {
        const out: Delta[] = [];
        for (const id of prompt.subjectIds)
          if (ctx.state.people[id] !== undefined)
            out.push({
              kind: 'planDeparture',
              personId: id,
              reason: 'rumour',
              atTurn: departureTurn(ctx, ctx.season.turn),
            });
        out.push(choiceOutcome(prompt, 'let-go', { people: members(ctx, prompt).length }));
        return out;
      },
    },
  ];
  return defineEvent({
    id: 'rumour',
    label: 'Rumour of a better valley',
    section: 'S19',
    baseWeight: c.baseChance,
    seasons: ['spring'],
    foreshadow: null,
    ledgerTag: null,
    weight: (ctx) =>
      Object.keys(ctx.state.people).length > 0 && settlementTrust(ctx.state) < c.trustBelow
        ? c.baseChance
        : 0,
    text: (ctx) =>
      eventEntry('rumour', { households: wavering(ctx, c.minHouseholds, c.maxHouseholds).length }),
    apply: (ctx) => {
      const households = wavering(ctx, c.minHouseholds, c.maxHouseholds);
      if (households.length === 0) return [];
      const subjects = households
        .map((h) => [...h.memberIds].sort()[0])
        .filter((id) => id !== undefined);
      return [
        eventPrompt(ctx, 'rumour', choices, {
          params: { households: households.length },
          subjectIds: subjects,
          defaultChoiceId: 'address',
        }),
      ];
    },
    choices,
    modifierTargets: [],
    modifiers: noModifiers,
  });
}
